import type { OpeningTrim } from "building-types/casing/openingTrimTypes";
import type { PolyLine2sWithPlane } from "building-types/walls/PolyLine2sWithPlane";
import type { AABB3 } from "geom/aabb3Types";
import type { Plane } from "geom/planeTypes";
import type { PolyLine2, PolyLine2Polygon } from "geom/polyLine2Types";
import type { PolyLine3 } from "geom/polyLine3";
import type { PolyLineEdgeType } from "geom/polyLineTypes";
import type { Rect2 } from "geom/rect2Types";

export const FRONT = "FRONT";
export const BACK = "BACK";

export type OpeningSideName = typeof FRONT | typeof BACK;

export interface OpeningSideLayer {
  union?: PolyLine2sWithPlane;
  /**
   * layer edges that belong only to this opening
   */
  lines?: PolyLine2sWithPlane;
}

export interface OpeningProfileLoops2 {
  /**
   * CCW resolved outline of the opening
   */
  opening2: PolyLine2;
  /**
   * Semantic geometry type for each edge in opening2.
   */
  opening2EdgeTypes?: readonly PolyLineEdgeType[];
  /**
   * CCW resolved outline of the opening with casing included
   */
  openingCasing2: PolyLine2;
  /**
   * CCW basic rectilinear outline of the opening
   */
  openingRect2: Rect2;
  /**
   * cache of opening2 offset by distance
   */
  openingOffsets2: { [distance: string]: PolyLine2 };
  /**
   * opening geometry per wall layer
   */
  openingSideLayers: { [layerId: string]: OpeningSideLayer };
}

export interface OpeningProfileLoops3 {
  opening3?: PolyLine3;
  openingCasing3?: PolyLine3;
  openingOffsets3: { [distance: string]: PolyLine3 };
  aabb3?: AABB3;
}

export interface OpeningSideContext {
  readonly condition?: "EXTERIOR" | "INTERIOR"; // may be unknown
  readonly openingPlane?: Plane;
}

export interface OpeningSide extends OpeningProfileLoops2, OpeningProfileLoops3 {
  readonly context: OpeningSideContext;
  readonly hasCasing?: boolean;
  // @todo(Andrew): this should be where the presence of wrapping is stored
  // readonly hasWrapping?: boolean;
  headHeight?: number;
  readonly trim: OpeningTrim[];
  /**
   * @deprecated use trim
   */
  readonly headCasing: PolyLine2[];
  /**
   * @deprecated use trim
   * the side casing type is a polygon because it is used for complex casing
   * types that include closed shapes like ellipses
   */
  readonly sideCasing: PolyLine2Polygon[];
  /**
   * @deprecated use trim
   */
  apronCasing?: PolyLine2;
  /**
   * @deprecated use trim
   */
  stoolCasing?: PolyLine2;
  shapes?: OpeningShapes;
  shapesCasing?: OpeningShapes;
  readonly startParam: number;
  readonly endParam: number;
  readonly centerParam: number;
}

export interface OpeningShapes {
  /**
   * CCW primary outline of the shape
   */
  outlineRectangular: Rect2;
  /**
   * CCW resolved outline of the shape
   */
  outline: PolyLine2;
  outlineEdgeTypes: readonly PolyLineEdgeType[];
  /**
   * CW outline of the shape
   */
  outlineReversed?: PolyLine2;
  /**
   * arch shape info - if shapes includes top arc
   */
  arcCurve?: PolyLine2;
  arcCurveEdgeTypes?: readonly PolyLineEdgeType[];
  /**
   * the unmodified arc curve for head trim (specifically when side trim is disabled)
   */
  arcCurveSimple?: PolyLine2;
  arcCurveSimpleEdgeTypes?: readonly PolyLineEdgeType[];
  arcOutline?: PolyLine2;
  arcOutlineReversed?: PolyLine2;
  arcHeight?: number;
  arcAngleRads?: number;
  arcCenterX?: number;
  arcCenterY?: number;
  arcRadius?: number;
  arcFactor?: number;
  /**
   * a line connecting two ends of an arc, if it exists
   */
  arcBar?: PolyLine2;
}
